import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Configure axios defaults
const setupAxiosInterceptors = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("cia_token"));
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);

  const fetchOnboardingStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies/onboarding`);
      // Check if company exists and has completed minimum setup
      const completed = response.data.completed || 
                       (response.data.steps?.company_created && 
                        response.data.steps?.first_client_added);
      setOnboardingCompleted(completed);
    } catch (error) {
      // If no company yet, onboarding is not completed
      setOnboardingCompleted(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem("cia_token");
    if (!storedToken) {
      setLoading(false);
      return;
    }
    
    setupAxiosInterceptors(storedToken);
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setToken(storedToken);
      
      // Fetch onboarding status for non-superadmin users
      if (response.data.role !== 'superadmin') {
        await fetchOnboardingStatus();
      } else {
        setOnboardingCompleted(true); // Superadmins don't need onboarding
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      // Only logout if it's an auth error
      if (error.response?.status === 401) {
        localStorage.removeItem("cia_token");
        delete axios.defaults.headers.common["Authorization"];
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardingStatus]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Ensure axios always has the latest token
  useEffect(() => {
    setupAxiosInterceptors(token);
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData, token: authToken } = response.data;
    
    localStorage.setItem("cia_token", authToken);
    setupAxiosInterceptors(authToken);
    setToken(authToken);
    setUser(userData);
    
    // Fetch onboarding status after login
    if (userData.role !== 'superadmin') {
      try {
        const onboardingRes = await axios.get(`${API}/companies/onboarding`);
        const completed = onboardingRes.data.completed || 
                         (onboardingRes.data.steps?.company_created && 
                          onboardingRes.data.steps?.first_client_added);
        setOnboardingCompleted(completed);
      } catch {
        setOnboardingCompleted(false);
      }
    } else {
      setOnboardingCompleted(true);
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { user: newUser, token: authToken } = response.data;
    
    localStorage.setItem("cia_token", authToken);
    setupAxiosInterceptors(authToken);
    setToken(authToken);
    setUser(newUser);
    setOnboardingCompleted(false); // New users always need onboarding
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("cia_token");
    setupAxiosInterceptors(null);
    setToken(null);
    setUser(null);
    setOnboardingCompleted(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };
  
  const completeOnboarding = () => {
    setOnboardingCompleted(true);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      onboardingCompleted,
      login, 
      register, 
      logout, 
      updateUser, 
      fetchUser,
      completeOnboarding,
      fetchOnboardingStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};
